import type { SchemaLoanRequestResolver_Get } from 'cpool-tables/backend-resolvers';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { OptionCheckbox } from './OptionCheckbox';
import { SalarySelect } from './SalarySelect';

type Props = {
    applicant: 1 | 2;
    lead: SchemaLoanRequestResolver_Get;
};

export const ApplicantRow: React.FC<Props> = ({ applicant, lead }) => {
    const { t } = useTranslation();

    const excluded = useWatch({ name: `dataSnippets.applicant${applicant}.excludeApplicant` });

    const applicant2DidNotAgreeDataProcessing = applicant === 2 && lead.antragsteller2?.dataProtectionAgreed === false;

    const applicant2Hidden = applicant === 2 && lead.coApplicantHidden;

    let disabledText = '';
    if (applicant2Hidden) {
        disabledText = t('LEAD_DETAILS_GENERAL.SECOND_APPLICANT_HIDDEN_WARNING');
    } else if (applicant2DidNotAgreeDataProcessing) {
        disabledText = t('LEAD_DETAILS_GENERAL.DATA_PROTECTION_AGREEMENT_WARNING');
    } else {
        disabledText = t('BANK_CREDIT_REQUEST.DATA_SHARING.APPLICANT_IS_EXCLUDED', { count: applicant });
    }

    const isDisabled = excluded || applicant2DidNotAgreeDataProcessing || applicant2Hidden;
    const isDisabledExcludeApplicant =
        !lead.antragsteller2 || lead.coApplicantHidden || lead.antragsteller2?.dataProtectionAgreed === false;

    return (
        <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-baseline gap-3">
                <p className="text-sm font-bold text-midnight">
                    {t(`BANK_CREDIT_REQUEST.DATA_SHARING.APPLICANT_TITLES.APPLICANT_${applicant}`)}
                </p>
                {isDisabled && <span className="text-xs text-grey">{disabledText}</span>}
            </div>
            <div className="grid grid-cols-2 items-center gap-x-6 gap-y-3 @4xl:grid-flow-col @4xl:grid-cols-[repeat(4,auto)] @4xl:grid-rows-2">
                <div className="col-span-2 @2xl:col-span-1">
                    <SalarySelect applicant={applicant} lead={lead} disabled={isDisabled} />
                </div>
                <OptionCheckbox
                    name={`dataSnippets.applicant${applicant}.excludeApplicant`}
                    translationPath="BANK_CREDIT_REQUEST.DATA_SHARING.EXCLUDE_APPLICANT"
                    disabled={isDisabledExcludeApplicant}
                />
                <OptionCheckbox
                    name={`dataSnippets.applicant${applicant}.rentFree`}
                    translationPath="BANK_CREDIT_REQUEST.DATA_SHARING.RENT_FREE"
                    disabled={isDisabled}
                />
                <OptionCheckbox
                    name={`dataSnippets.applicant${applicant}.withoutRentIncome`}
                    translationPath="BANK_CREDIT_REQUEST.DATA_SHARING.WITHOUT_RENT_INCOME"
                    disabled={isDisabled}
                />
                <OptionCheckbox
                    name={`dataSnippets.applicant${applicant}.withoutMaintenanceIncome`}
                    translationPath="BANK_CREDIT_REQUEST.DATA_SHARING.WITHOUT_MAINTENANCE_INCOME"
                    disabled={isDisabled}
                />
                <OptionCheckbox
                    name={`dataSnippets.applicant${applicant}.withoutSidejob`}
                    translationPath="BANK_CREDIT_REQUEST.DATA_SHARING.WITHOUT_SIDE_JOB"
                    disabled={isDisabled}
                />
                <OptionCheckbox
                    name={`dataSnippets.applicant${applicant}.withoutOtherIncome`}
                    translationPath="BANK_CREDIT_REQUEST.DATA_SHARING.WITHOUT_OTHER_INCOME"
                    disabled={isDisabled}
                />
            </div>
        </div>
    );
};
